import React, { useState, useEffect } from 'react';
import { MobileLine, HistoricoVinculo } from '../types/MobileLine';
import api from '../services/api';
import { encontrarFaturaVencidaMaisAntiga, formatarData } from '../utils/faturaUtils';

export interface LinhaCardProps {
  line: MobileLine;
  onVincular: () => void;
  onExcluir: (numero: string) => void;
  onAtualizarFaturas: (numero: string) => void;
  onRemoverVinculo: (numero: string) => void;
  onAtualizarLinha: (numero: string, data: Partial<MobileLine>, silencioso?: boolean) => void;
  openModal: (title: string, message: string, onConfirm?: () => void, showConfirmButton?: boolean) => void;
}

const LinhaCard: React.FC<LinhaCardProps> = ({ 
  line, 
  onVincular, 
  onExcluir, 
  onAtualizarFaturas,
  onRemoverVinculo,
  onAtualizarLinha,
  openModal 
}) => {
  const [mostrarAviso, setMostrarAviso] = useState(false);
  const [mostrarAvisoBloqueio, setMostrarAvisoBloqueio] = useState(false);
  const [expandido, setExpandido] = useState(false);
  const [editandoValorMensal, setEditandoValorMensal] = useState(false);
  const [editandoGigas, setEditandoGigas] = useState(false);
  const [valorMensalTemp, setValorMensalTemp] = useState(line.valorMensal?.toString() || '');
  const [gigasTemp, setGigasTemp] = useState(line.gigasDisponiveis?.toString() || '');
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  // Parse histórico
  let historico: HistoricoVinculo[] = [];
  try {
    historico = line.historico ? JSON.parse(line.historico) : [];
  } catch (e) {
    historico = [];
  }

  // Formatar data para exibição
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };


  
  useEffect(() => {
    const checkLineStatus = async () => {
      try {
        const statusData = await api.getLineStatus(line.numero);
        const jaConfirmadoLiberacao = statusData?.liberado || false;
        const jaConfirmadoBloqueio = statusData?.bloqueado || false;

        const agora = new Date();
        const dataAdimplencia = line.dataAdimplencia ? new Date(line.dataAdimplencia) : null;
        const dataInadimplencia = line.dataInadimplencia ? new Date(line.dataInadimplencia) : null;

        // Aviso de liberação (linha adimplente)
        // Mostrar se: é adimplente, não foi confirmado liberação, e (não tem data OU passou menos de 72h)
        const diffHorasAdimplencia = dataAdimplencia
          ? (agora.getTime() - dataAdimplencia.getTime()) / (1000 * 60 * 60)
          : 0; // Se não tiver data, considera como "agora mesmo" (0 horas)

        const shouldShowAlertLiberacao =
          line.status === 'adimplente' &&
          !jaConfirmadoLiberacao &&
          diffHorasAdimplencia <= 72;

        // Aviso de bloqueio (linha inadimplente)
        // Mostrar se: é inadimplente, não foi confirmado bloqueio, e (não tem data OU passou menos de 72h)
        const diffHorasInadimplencia = dataInadimplencia
          ? (agora.getTime() - dataInadimplencia.getTime()) / (1000 * 60 * 60)
          : 0; // Se não tiver data, considera como "agora mesmo" (0 horas)

        const shouldShowAlertBloqueio =
          line.status === 'inadimplente' &&
          !jaConfirmadoBloqueio &&
          diffHorasInadimplencia <= 72;

        setMostrarAviso(shouldShowAlertLiberacao);
        setMostrarAvisoBloqueio(shouldShowAlertBloqueio);
      } catch (error) {
        console.error('Erro ao verificar status da linha:', error);
      }
    };
    
    checkLineStatus();
  }, [line.status, line.dataAdimplencia, line.dataInadimplencia, line.numero]);

  // Debug: Log das faturas
  useEffect(() => {
    if (line.numero) {
      const faturasContent = typeof line.faturas === 'string' 
        ? (line.faturas.length > 100 ? line.faturas.substring(0, 100) + '...' : line.faturas)
        : JSON.stringify(line.faturas).substring(0, 100) + '...';
      
      console.log(`[LinhaCard Debug] Linha ${line.numero}:`, {
        status: line.status,
        temFaturas: !!line.faturas,
        tipoFaturas: typeof line.faturas,
        faturasLength: Array.isArray(line.faturas) ? line.faturas.length : (typeof line.faturas === 'string' ? line.faturas.length : 0),
        faturasContent,
        bloqueioConfirmado: line.bloqueioConfirmado,
        dataBloqueio: line.dataBloqueio
      });
    }
  }, [line.numero, line.faturas, line.status, line.bloqueioConfirmado, line.dataBloqueio]); 

  const confirmarLiberacao = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.updateLineStatus(line.numero, true);
      setMostrarAviso(false);
    } catch (error) {
      console.error('Erro ao confirmar liberação:', error);
    }
  };

  const confirmarBloqueio = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.updateLineStatus(line.numero, undefined, true);
      // Também salvar a data de bloqueio na linha e notificar o pai
      const dataBloqueio = new Date().toISOString();
      await api.updateLine(line.numero, { dataBloqueio });
      setMostrarAvisoBloqueio(false);
      // Notificar o pai para atualizar a lista (linha vai para "Bloqueados") - modo silencioso
      onAtualizarLinha(line.numero, { dataBloqueio }, true);
    } catch (error) {
      console.error('Erro ao confirmar bloqueio:', error);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExcluir(line.numero);
  };

  const handleVincularClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onVincular();
  };

  const handleAtualizarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAtualizarFaturas(line.numero);
  };

  const handleRemoverVinculoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoverVinculo(line.numero);
  };

  const handleSalvarValorMensal = (e: React.MouseEvent) => {
    e.stopPropagation();
    const valor = parseFloat(valorMensalTemp);
    if (!isNaN(valor) && valor >= 0) {
      onAtualizarLinha(line.numero, { valorMensal: valor });
      setEditandoValorMensal(false);
    } else {
      openModal('Erro', 'Digite um valor válido', undefined, false);
    }
  };

  const handleSalvarGigas = (e: React.MouseEvent) => {
    e.stopPropagation();
    const gigas = parseFloat(gigasTemp);
    if (!isNaN(gigas) && gigas >= 0) {
      onAtualizarLinha(line.numero, { gigasDisponiveis: gigas });
      setEditandoGigas(false);
    } else {
      openModal('Erro', 'Digite uma quantidade válida de gigas', undefined, false);
    }
  };

  const handleCancelarValorMensal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValorMensalTemp(line.valorMensal?.toString() || '');
    setEditandoValorMensal(false);
  };

  const handleCancelarGigas = (e: React.MouseEvent) => {
    e.stopPropagation();
    setGigasTemp(line.gigasDisponiveis?.toString() || '');
    setEditandoGigas(false);
  };

  // Formatar número de telefone
  const formatPhoneNumber = (numero: string) => {
    const cleaned = numero.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numero;
  };

  let faturas = [];
  try {
    const faturasData = line.faturas;
    if (typeof faturasData === 'string' && faturasData.trim()) {
      faturas = JSON.parse(faturasData);
    } else if (Array.isArray(faturasData)) {
      faturas = faturasData;
    }
  } catch (error) {
    console.error('Erro ao fazer parse de faturas:', error);
    faturas = [];
  }
  
  const faturasVencidas = faturas.filter((f: any) => 
    f.status !== 'Pago' && new Date(f.vencimento_atualizado || f.vencimento) < new Date()
  );

  return (
    <div
      className="relative bg-zinc-700 border border-zinc-600 rounded-lg p-4 mb-2 hover:bg-zinc-650 transition-all shadow-md cursor-pointer"
      onClick={() => setExpandido(!expandido)}
    >
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <p className="text-white font-bold text-lg">📱 {formatPhoneNumber(line.numero)}</p>
          {line.nome && (
            <p className="text-gray-300 text-sm mt-1">
              <strong>Cliente:</strong> {line.nome}
            </p>
          )}
          {line.contrato && (
            <p className="text-gray-400 text-xs">
              Contrato: {line.contrato}
            </p>
          )}
          {/* Tag Bloqueado desde - boleto vencido mais antigo */}
          {line.status === 'inadimplente' && line.bloqueioConfirmado && faturas.length > 0 && (() => {
            try {
              const faturaMaisAntiga = encontrarFaturaVencidaMaisAntiga(faturas as any);
              if (faturaMaisAntiga) {
                const dataVencimento = faturaMaisAntiga.vencimento;
                const countFaturasVencidas = faturas.filter((f: any) => 
                  f.status !== 'Pago' && new Date(f.vencimento) < new Date()
                ).length;
                return (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-900/50 border border-red-600 rounded text-red-400 text-xs font-semibold">
                      🔒 Bloqueado: {countFaturasVencidas} fatura{countFaturasVencidas !== 1 ? 's' : ''} em atraso desde {formatarData(dataVencimento)}
                    </span>
                  </div>
                );
              }
              return null;
            } catch (e) {
              return null;
            }
          })()}

          {/* Tag Inadimplente desde (boleto vencido mais antigo) */}
          {line.status === 'inadimplente' && !line.bloqueioConfirmado && faturas.length > 0 && (() => {
            try {
              const faturaMaisAntiga = encontrarFaturaVencidaMaisAntiga(faturas as any);
              if (faturaMaisAntiga) {
                // Usar sempre vencimento (data real), não vencimento_atualizado
                const dataVencimento = faturaMaisAntiga.vencimento;
                return (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-900/50 border border-yellow-600 rounded text-yellow-400 text-xs font-semibold">
                      ⏰ Inadimplente desde: {formatarData(dataVencimento)}
                    </span>
                  </div>
                );
              }
              return null;
            } catch (e) {
              return null;
            }
          })()}
        </div>

        {/* Botão Excluir */}
        <button
          className="text-red-400 hover:text-red-600 text-xl p-1 rounded-full hover:bg-zinc-600 transition"
          onClick={handleDeleteClick}
          title="Excluir Linha"
        >
          🗑️
        </button>
      </div>

      {/* Alerta de Liberação */}
      {mostrarAviso && (
        <div className="mt-3 px-3 py-2 bg-yellow-500 text-zinc-900 rounded-md font-semibold text-sm flex items-center justify-between">
          <span>🔓 Desbloquear linha</span>
          <button
            className="ml-2 px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-xs font-bold"
            onClick={confirmarLiberacao}
          >
            OK
          </button>
        </div>
      )}

      {/* Alerta de Bloqueio */}
      {mostrarAvisoBloqueio && (
        <div className="mt-3 px-3 py-2 bg-yellow-500 text-zinc-900 rounded-md font-semibold text-sm flex items-center justify-between">
          <span>⚠️ Bloquear linha (acompanhar em outro sistema)</span>
          <button
            className="ml-2 px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-xs font-bold"
            onClick={confirmarBloqueio}
          >
            OK
          </button>
        </div>
      )}

      {/* Botão de Vincular (se sem usuário) */}
      {line.status === 'semUsuario' && (
        <button
          onClick={handleVincularClick}
          className="mt-3 w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-semibold text-sm"
        >
          Vincular CPF/Contrato
        </button>
      )}

      {/* Informações Expandidas */}
      {expandido && line.status !== 'semUsuario' && (
        <div className="mt-3 pt-3 border-t border-zinc-600 text-sm space-y-2">
          {line.cpfCnpj && <p className="text-gray-300"><strong>CPF/CNPJ:</strong> {line.cpfCnpj}</p>}
          
          {/* Dados do SGP (somente leitura) */}
          {line.plano && (
            <p className="text-gray-300">
              <strong>Plano SGP:</strong> {line.plano}
            </p>
          )}
          {line.valorPlano && (
            <p className="text-gray-300">
              <strong>Valor SGP:</strong> R$ {line.valorPlano.toFixed(2)}
            </p>
          )}

          {/* Divisor */}
          <div className="border-t border-zinc-700 pt-2 mt-2">
            <p className="text-xs text-gray-400 mb-2">📝 Dados Editáveis</p>
          </div>

          {/* Campo Valor Mensal Editável */}
          <div className="flex items-center gap-2">
            <strong className="text-gray-300 min-w-[80px]">Valor Mensal:</strong>
            {editandoValorMensal ? (
              <div className="flex-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  step="0.01"
                  value={valorMensalTemp}
                  onChange={(e) => setValorMensalTemp(e.target.value)}
                  className="flex-1 px-2 py-1 bg-zinc-800 text-white rounded border border-zinc-600 text-xs"
                  placeholder="Ex: 30.00"
                />
                <button onClick={handleSalvarValorMensal} className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs">✓</button>
                <button onClick={handleCancelarValorMensal} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs">✗</button>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-between">
                <span className="text-gray-300">
                  {line.valorMensal ? `R$ ${line.valorMensal.toFixed(2)}` : 'Não definido'}
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); setEditandoValorMensal(true); }} 
                  className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                >
                  ✏️ Editar
                </button>
              </div>
            )}
          </div>

          {/* Campo Gigas Editável */}
          <div className="flex items-center gap-2">
            <strong className="text-gray-300 min-w-[80px]">Gigas:</strong>
            {editandoGigas ? (
              <div className="flex-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  step="1"
                  value={gigasTemp}
                  onChange={(e) => setGigasTemp(e.target.value)}
                  className="flex-1 px-2 py-1 bg-zinc-800 text-white rounded border border-zinc-600 text-xs"
                  placeholder="Ex: 100"
                />
                <button onClick={handleSalvarGigas} className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs">✓</button>
                <button onClick={handleCancelarGigas} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs">✗</button>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-between">
                <span className="text-gray-300">
                  {line.gigasDisponiveis ? `${line.gigasDisponiveis} GB` : 'Não definido'}
                </span>
                <button 
                  onClick={(e) => { e.stopPropagation(); setEditandoGigas(true); }} 
                  className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                >
                  ✏️ Editar
                </button>
              </div>
            )}
          </div>

          {line.loginPPPoE && <p className="text-gray-300"><strong>Login:</strong> {line.loginPPPoE}</p>}
          {line.senhaPPPoE && <p className="text-gray-300"><strong>Senha:</strong> {line.senhaPPPoE}</p>}
          
          {faturasVencidas.length > 0 && (
            <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-700">
              <p className="text-red-400 font-semibold">⚠️ {faturasVencidas.length} fatura(s) vencida(s)</p>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleAtualizarClick}
              className="flex-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold"
            >
              Atualizar Faturas
            </button>
            <button
              onClick={handleVincularClick}
              className="flex-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-semibold"
            >
              Alterar Vínculo
            </button>
          </div>

          <div className="flex gap-2 mt-2">
            <button
              onClick={handleRemoverVinculoClick}
              className="flex-1 px-3 py-1 bg-zinc-600 hover:bg-zinc-700 text-white rounded text-xs font-semibold"
            >
              Remover Vínculo
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setMostrarHistorico(!mostrarHistorico); }}
              className={`flex-1 px-3 py-1 ${historico.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-600 hover:bg-gray-700'} text-white rounded text-xs font-semibold`}
            >
              📜 Histórico ({historico.length})
            </button>
          </div>

          {/* Histórico de Vínculos */}
          {mostrarHistorico && (
            <div className="mt-3 p-3 bg-zinc-800 rounded-lg border border-zinc-600">
              <h4 className="text-amber-400 font-semibold text-sm mb-2">📜 Histórico de Vínculos</h4>
              {historico.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {historico.map((h, index) => (
                    <div key={index} className="p-2 bg-zinc-700 rounded text-xs">
                      <p className="text-white font-semibold">{h.nome}</p>
                      <p className="text-gray-400">CPF/CNPJ: {h.cpfCnpj}</p>
                      <p className="text-gray-400">Contrato: {h.contrato}</p>
                      <p className="text-gray-500 text-[10px]">
                        Vinculado em: {formatDate(h.dataVinculo)} | Removido em: {formatDate(h.dataRemocao)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-xs italic">Nenhum vínculo anterior registrado</p>
              )}
            </div>
          )}
        </div>
      )}

      {expandido && line.status === 'semUsuario' && (
        <p className="mt-2 text-xs text-gray-400 italic text-center">
          Vincule um CPF/Contrato para visualizar mais informações
        </p>
      )}

      {!expandido && (
        <p className="mt-2 text-xs text-gray-500 text-center">
          Clique para expandir
        </p>
      )}
    </div>
  );
};

export default LinhaCard;
